'use client';

import { useState } from 'react';
import { type RecommendationProduct } from '@/common/types/recommended-products';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide, type SwiperClass } from 'swiper/react';
import Button from '@/common/components/button';
import Skeleton from '@/common/components/skeleton';
import 'swiper/css';
import ArrowLeftIcon from '~/public/assets/images/svg/arrow-left-gray.svg';
import Product from './product.component';

interface RecommendationSectionProps {
  title: string;
  products: RecommendationProduct[] | null | undefined;
  isError: boolean;
  isLoading: boolean;
}

function RecommendationSection({
  title,
  products,
  isError,
  isLoading,
}: RecommendationSectionProps) {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const syncNavigationState = (instance: SwiperClass) => {
    setIsBeginning(instance.isBeginning);
    setIsEnd(instance.isEnd);
  };

  if (!isLoading && (isError || !products?.length)) return null;

  return (
    <section className="w-full">
      <div className="mb-5 flex items-start justify-between px-4 text-xs font-normal lg:mb-6 lg:px-0">
        <h2 className="font-medium text-black-default lg:!text-xl lg:!font-semibold">
          {title}
        </h2>
        <div className="flex items-center gap-2 lg:gap-3">
          <Button
            type="gray"
            className="flex !h-8 w-8 items-center justify-center rounded-lg !border-solid border-transparent !p-2 disabled:!border-silver-300 disabled:bg-white lg:!h-9 lg:w-9"
            disabled={isBeginning}
            onClick={() => swiper?.slidePrev()}
          >
            <ArrowLeftIcon className="rotate-180 text-black-default" />
          </Button>
          <Button
            type="gray"
            className="flex !h-8 w-8 items-center justify-center rounded-lg !border-solid border-transparent !p-2 disabled:!border-silver-300 disabled:bg-white lg:!h-9 lg:w-9"
            disabled={isEnd}
            onClick={() => swiper?.slideNext()}
          >
            <ArrowLeftIcon className="text-black-default" />
          </Button>
        </div>
      </div>

      {products?.length ? (
        <Swiper
          breakpoints={{
            0: {
              slidesPerView: 2.1,
              spaceBetween: 12,
              slidesOffsetBefore: 16,
              slidesOffsetAfter: 16,
            },
            1024: {
              slidesPerView: 5,
              spaceBetween: 27,
              slidesOffsetBefore: 0,
              slidesOffsetAfter: 0,
            },
          }}
          onSwiper={(instance) => {
            setSwiper(instance);
            syncNavigationState(instance);
          }}
          onSlideChange={syncNavigationState}
          modules={[Navigation]}
        >
          {products.map((product) => (
            <SwiperSlide key={`${product.productId}-${product.slug}`}>
              <div
                data-id="product-click-other-product-v1"
                className="mb-3 box-border flex h-[206px] cursor-pointer flex-col overflow-hidden border-none lg:h-auto"
              >
                <Product itemDetail={product} />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      ) : null}

      {isLoading ? (
        <div className="flex gap-3 overflow-hidden lg:gap-6">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="min-w-[calc(50%-6px)] lg:min-w-0 lg:flex-1"
            >
              <Skeleton type="product-item" />
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export default RecommendationSection;
